import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Check } from 'lucide-react';
import PolicyConsentModal from '../../components/policy/policy-consent-modal';
import { getConsentStatus } from '../../api/user/consent.api';
import { getAffiliatePartnerStatus } from '../../api/affiliate/affiliate.api';
import { RootState } from '../../configuration';
import { getAffRef } from '../../utils/common/aff-ref';
import {
  CabPlusStatus,
  getCabPlusStatus,
  buyCabPlus,
  CabPlusTier,
  CabPlusTierBenefit,
} from '../../api/cab-plus/cab-plus.api';
import { CabPlusSlot, getCabPlusSlot } from '../../api/payment/membership.api';
import { formatMemberUntil } from '../../utils/membership';

/**
 * Trang "Gói mua nâng cấp" CAB+ — dựng theo design Figma (node 625-10852).
 * Nội dung tĩnh (chưa có BE subscription cho CAB+): 3 gói Lite/Plus/Pro +
 * bảng so sánh tính năng + bảng phân bậc Avatar & Vật phẩm (giá tính bằng Ca,
 * khớp seeder store-rarity đã deploy). Nút "Nâng cấp ngay" là placeholder tới khi
 * có luồng subscribe ở backend.
 */

// Màu bậc hiếm — khớp StoreSeeder backend (Common→Mythic)
const RARITY = {
  common: '#9CA3AF',
  rare: '#3B82F6',
  epic: '#8B5CF6',
  legendary: '#F59E0B',
  mythic: 'linear-gradient(135deg,#fb923c 0%,#f43f5e 50%,#a855f7 100%)',
};

type Feature = { label: string; value: string; tail?: string };

interface Tier {
  key: string;
  name: string;
  price: string;
  save: string;
  badge?: string;
  recommended?: boolean;
  avatar: { text: string; dot: string };
  features: Feature[];
}

const TIERS: Tier[] = [
  { key: 'lite', name: 'Gói Lite', price: '100.000 Ca', save: '', avatar: { text: 'Hiếm (Xanh)', dot: RARITY.rare }, features: [] },
  { key: 'plus', name: 'Gói Plus', price: '200.000 Ca', save: '', badge: 'Plus ★', avatar: { text: 'Sử thi (Tím)', dot: RARITY.epic }, features: [] },
  {
    key: 'pro',
    name: 'Gói Pro',
    price: '500.000 Ca',
    save: 'Đã gồm không quảng cáo',
    recommended: true,
    avatar: { text: 'Huyền Thoại (Vàng)', dot: RARITY.legendary },
    features: [],
  },
];

const TIER_ORDER: CabPlusTier[] = ['lite', 'plus', 'pro'];
const TIER_RANK: Record<CabPlusTier, number> = { lite: 1, plus: 2, pro: 3 };
const TIER_NAME: Record<CabPlusTier, string> = { lite: 'Cab+ Lite', plus: 'Cab+ Plus', pro: 'Cab+ Pro' };

// Giá trị mặc định khi API chưa trả benefits/giá.
const DEFAULT_BENEFITS: Record<CabPlusTier, CabPlusTierBenefit> = {
  lite: { aiTurns: 100, datingRequests: 3, avatarRarity: 'rare' },
  plus: { aiTurns: 300, datingRequests: 5, avatarRarity: 'epic' },
  pro: { aiTurns: 400, datingRequests: 6, avatarRarity: 'legendary' },
};
const DEFAULT_PRICE: Record<CabPlusTier, number> = { lite: 100000, plus: 200000, pro: 500000 };

const fmtNum = (n: number) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const fmtDate = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const buildFeatures = (key: CabPlusTier, b: CabPlusTierBenefit): Feature[] => {
  const common: Feature[] = [
    { label: 'Super Vote: ', value: 'sắp có' },
    { label: 'Lượt kết nối Hẹn hò (cộng dồn): ', value: String(b.datingRequests), tail: ' / tháng đã mua' },
    { label: '', value: String(b.aiTurns), tail: ' lượt hỏi trợ lý mỗi tháng' },
  ];
  if (key !== 'pro') return common;
  return [
    { label: '', value: 'Ẩn toàn bộ quảng cáo', tail: ' (Cab+ Pro)' },
    { label: '', value: 'Xem offline 5 Creator', tail: ' (sắp có)' },
    { label: '', value: 'Mỗi tháng tặng 1 lượt hội viên kênh' },
    ...common,
  ];
};

const buildComparison = (bn: Record<CabPlusTier, CabPlusTierBenefit>): {
  label: string;
  cells: (string | { dot: string; text: string })[];
  strong?: boolean;
}[] => [
  {
    label: 'Khung avatar theo hạn gói',
    cells: [
      { dot: RARITY.rare, text: 'Hiếm' },
      { dot: RARITY.epic, text: 'Sử thi' },
      { dot: RARITY.legendary, text: 'Huyền Thoại' },
    ],
  },
  { label: 'Super Vote', cells: ['sắp có', 'sắp có', 'sắp có'] },
  { label: 'Lượt kết nối Hẹn hò (cộng dồn)', cells: TIER_ORDER.map((k) => String(bn[k].datingRequests)) },
  { label: 'Lượt hỏi trợ lý mỗi tháng', cells: TIER_ORDER.map((k) => `${bn[k].aiTurns} lượt`) },
];

interface TierRow {
  name: string;
  dot: string;
  color: string;
  mechanism: string;
  mechanismClass?: string;
  price: string;
  limit: string;
  limitClass?: string;
}

const AVATAR_TIERS: TierRow[] = [
  { name: 'Cổ điển', dot: RARITY.common, color: 'Xám', mechanism: 'Hạn 60 ngày', price: '80.000 Ca', limit: '—' },
  { name: 'Hiếm', dot: RARITY.rare, color: 'Xanh', mechanism: 'Hạn 60 ngày', price: '150.000 Ca', limit: '—' },
  { name: 'Sử thi', dot: RARITY.epic, color: 'Tím', mechanism: 'Hạn 60 ngày', price: '300.000 Ca', limit: '—' },
  {
    name: 'Huyền thoại',
    dot: RARITY.legendary,
    color: 'Vàng',
    mechanism: 'Hạn 1 năm',
    price: '2.000.000 Ca',
    limit: '—',
  },
  {
    name: 'Thần thoại',
    dot: RARITY.mythic,
    color: 'Gradient',
    mechanism: 'Hiệu ứng ava đặc quyền, hạn 1 năm',
    price: '10.000.000 Ca',
    limit: 'Mỗi tháng mở 1 suất · tối đa 12/năm',
    limitClass: 'text-[#ff8a8a] font-semibold',
  },
];

const Dot: React.FC<{ color: string; size?: number }> = ({ color, size = 14 }) => (
  <span
    className="inline-block shrink-0 rounded-full"
    style={{ width: size, height: size, background: color }}
  />
);

const CabPlusPage: React.FC = () => {
  // Cab+ Pro (không quảng cáo + xem offline) — mua bằng Ca ví (BE thật).
  const [status, setStatus] = useState<CabPlusStatus | null>(null);
  const [months, setMonths] = useState(1);
  const [buying, setBuying] = useState(false);
  const [confirmTier, setConfirmTier] = useState<'lite' | 'plus' | null>(null);

  // Affiliate nội bộ: ref từ URL (?ref=) để quy công người giới thiệu khi mua; + link giới thiệu của CHÍNH user (nếu là CTV).
  const profile = useSelector((s: RootState) => s.features.auth.profile) as any;
  const myUserId = profile?.userId || profile?.id || '';
  const [refUserId] = useState<string>(() => new URLSearchParams(window.location.search).get('ref') || getAffRef() || '');
  const [isAffiliate, setIsAffiliate] = useState(false);
  useEffect(() => {
    getAffiliatePartnerStatus().then((p) => setIsAffiliate(p?.status === 'Approved')).catch(() => undefined);
  }, []);
  const myRefLink = myUserId ? `${window.location.origin}/account/cabplus?ref=${myUserId}` : '';

  const loadStatus = () => {
    getCabPlusStatus()
      .then(setStatus)
      .catch(() => undefined);
  };
  useEffect(loadStatus, []);

  // Lượt hội viên kênh tháng này (chỉ khi đang là Cab+ Pro); lỗi hoặc chưa có kỳ thì không hiện gì.
  const [slot, setSlot] = useState<CabPlusSlot['current']>(null);
  const isPremium = !!status?.isPremium;
  useEffect(() => {
    if (!isPremium) return;
    getCabPlusSlot()
      .then((r) => setSlot(r?.current ?? null))
      .catch(() => setSlot(null));
  }, [isPremium]);

  const [cabplusConsent, setCabplusConsent] = useState<boolean | null>(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  useEffect(() => {
    getConsentStatus(['cabplus'])
      .then((list) => setCabplusConsent(!!list.find((x) => x.key === 'cabplus')?.accepted))
      .catch(() => undefined);
  }, []);

  const refForBuy = () => (refUserId && refUserId !== myUserId ? refUserId : undefined);

  const doBuyPro = async () => {
    try {
      setBuying(true);
      // Chỉ gửi ref khi khác chính mình (chống tự giới thiệu).
      const res = await buyCabPlus({ tier: 'pro', months, refUserId: refForBuy() });
      setStatus(res);
      toast.success('Đã kích hoạt Cab+ Pro! Trải nghiệm sạch không quảng cáo.');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Mua Cab+ Pro thất bại (kiểm tra số dư Ca).');
    } finally {
      setBuying(false);
    }
  };

  const doBuyTier = async (tier: 'lite' | 'plus') => {
    try {
      setBuying(true);
      const res = await buyCabPlus({ tier, months: 1, refUserId: refForBuy() });
      setStatus(res);
      setConfirmTier(null);
      toast.success(`Đã kích hoạt ${TIER_NAME[tier]} 1 tháng.`);
      loadStatus();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || `Mua ${TIER_NAME[tier]} thất bại (kiểm tra số dư Ca).`);
    } finally {
      setBuying(false);
    }
  };

  // Điều khoản đang chờ đồng ý cho gói nào (để mua tiếp sau khi đồng ý).
  const [consentFor, setConsentFor] = useState<CabPlusTier>('pro');

  // Mua Cab+ Pro: chưa đồng ý điều khoản thì hiện điều khoản trước.
  const handleBuyPro = async () => {
    if (!cabplusConsent) {
      setConsentFor('pro');
      setPolicyOpen(true);
      return;
    }
    await doBuyPro();
  };

  // Lite/Plus: đồng ý điều khoản (nếu chưa) rồi mở hộp xác nhận.
  const handleOpenConfirm = (tier: 'lite' | 'plus') => {
    if (!cabplusConsent) {
      setConsentFor(tier);
      setPolicyOpen(true);
      return;
    }
    setConfirmTier(tier);
  };

  const curTier: CabPlusTier | null = status?.tier ?? (status?.isPremium ? 'pro' : null);
  const curRank = curTier ? TIER_RANK[curTier] : 0;
  const curUntilStr = fmtDate(status?.tierUntil ?? (curTier === 'pro' ? status?.premiumUntil : null));
  const benefits: Record<CabPlusTier, CabPlusTierBenefit> = {
    lite: { ...DEFAULT_BENEFITS.lite, ...status?.benefits?.lite },
    plus: { ...DEFAULT_BENEFITS.plus, ...status?.benefits?.plus },
    pro: { ...DEFAULT_BENEFITS.pro, ...status?.benefits?.pro },
  };
  const priceOf = (k: CabPlusTier) =>
    (k === 'lite' ? status?.litePriceCa : k === 'plus' ? status?.plusPriceCa : status?.pricePerMonthCa) || DEFAULT_PRICE[k];
  const comparison = buildComparison(benefits);

  const untilStr = status?.premiumUntil
    ? new Date(status.premiumUntil).toLocaleDateString('vi-VN')
    : null;
  // Ưu đãi mua nhiều tháng: lấy từ BE (fallback tính tuyến tính nếu thiếu).
  const plans = status?.plans ?? [];
  const monthOptions = plans.length ? plans.map((p) => p.months) : [1, 3, 6, 12];
  const selectedPlan = plans.find((p) => p.months === months);
  const total = selectedPlan?.totalPriceCa ?? (status?.pricePerMonthCa || 0) * months;
  const originalTotal = selectedPlan?.originalTotalCa ?? total;
  const discountPercent = selectedPlan?.discountPercent ?? 0;

  return (
    <div className="mx-auto flex max-w-[1200px] flex-col gap-10 px-4 py-6 md:px-10">
      {/* Header */}
      <div>
        <h1 className="text-[26px] font-bold leading-tight text-[#002740] dark:text-white md:text-[30px]">
          Các gói nâng cấp
        </h1>
        <p className="mt-2 text-[15px] text-[#58657a] dark:text-slate-400">
          Chọn gói phù hợp để mở thêm tính năng và làm trang của bạn nổi bật hơn.
        </p>
      </div>

      {/* CTV (affiliate): link giới thiệu CAB+ — có người mua qua link thì nhận hoa hồng (giữ 30 ngày rồi cộng Ca). */}
      {isAffiliate && myRefLink && (
        <div className="rounded-2xl border border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 p-4">
          <div className="font-semibold text-sky-700 dark:text-sky-300 mb-1">🔗 Giới thiệu CAB+ cho bạn bè</div>
          <p className="text-sm text-[#58657a] dark:text-slate-400 mb-2">
            Gửi link này cho bạn bè. Ai mua Cab+ Pro từ link của bạn thì bạn được hoa hồng. Tiền vào ví sau 30 ngày và rút được như bình thường.
          </p>
          <div className="flex items-center gap-2">
            <input readOnly value={myRefLink} className="flex-1 rounded-lg border border-sky-200 dark:border-sky-500/30 bg-white dark:bg-boxdark px-3 py-2 text-sm dark:text-white" />
            <button
              onClick={() => { navigator.clipboard?.writeText(myRefLink).then(() => toast.success('Đã copy link giới thiệu')).catch(() => {}); }}
              className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700"
            >
              Copy
            </button>
          </div>
        </div>
      )}

      {/* Đang mua qua link giới thiệu của người khác → cho biết (minh bạch). */}
      {refUserId && refUserId !== myUserId && (
        <p className="text-xs text-emerald-600 dark:text-emerald-400">✔ Bạn đang mua qua link giới thiệu của một cộng tác viên.</p>
      )}

      {/* Pricing cards — Gói Pro = Cab+ Pro thật (mua bằng Ca, không quảng cáo) */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {TIERS.map((tierBase) => {
          const tierKey = tierBase.key as CabPlusTier;
          const tier = { ...tierBase, price: `${fmtNum(priceOf(tierKey))} Ca`, features: buildFeatures(tierKey, benefits[tierKey]) };
          const dark = tier.recommended;
          const isCurrent = curTier === tierKey;
          const isLower = curRank > TIER_RANK[tierKey];
          const isHigher = curRank > 0 && curRank < TIER_RANK[tierKey];
          return (
            <div
              key={tier.key}
              className={
                'flex flex-col justify-between rounded-[20px] p-8 ' +
                (dark
                  ? 'border-2 border-[#0255ff] bg-[#19052f]'
                  : 'border border-[#eaecf0] bg-white dark:border-slate-700 dark:bg-slate-800')
              }
            >
              <div className="flex flex-col gap-6">
                {/* Title + badge */}
                <div className="flex items-center justify-between">
                  <h2
                    className={
                      "text-[20px] font-bold " +
                      // dark card = bg-[#19052f]; !text-white beats global.css's
                      // heading !text-black in light mode (plain text-white loses to !important).
                      (dark ? '!text-white' : 'text-[#002740] dark:text-white')
                    }
                  >
                    {tier.name}
                  </h2>
                  {tier.recommended && (
                    <span className="rounded-full bg-[#0255ff] px-2.5 py-1 text-[12px] font-semibold text-white">
                      Recommended
                    </span>
                  )}
                  {tier.badge && (
                    <span className="rounded-full border border-primary/40 px-2.5 py-0.5 text-[12px] font-semibold text-primary">
                      {tier.badge}
                    </span>
                  )}
                </div>

                {/* Price */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-baseline gap-1">
                    <span
                      className={
                        "text-[32px] font-bold leading-none " +
                        (dark ? 'text-white' : 'text-[#002740] dark:text-white')
                      }
                    >
                      {tier.price}
                    </span>
                    <span className={dark ? 'text-[14px] text-white/70' : 'text-[14px] text-[#58657a] dark:text-slate-400'}>
                      /tháng
                    </span>
                  </div>
                  {tier.save && (
                    <span className={'text-[14px] font-semibold ' + (dark ? 'text-[#a8d7ff]' : 'text-[#0255ff] dark:text-[#5b8cff]')}>
                      {tier.save}
                    </span>
                  )}
                </div>

                {/* Divider */}
                <div className={'h-px w-full ' + (dark ? 'bg-white/10' : 'bg-[#eaecf0] dark:bg-slate-700')} />

                {/* Features */}
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <Dot color={tier.avatar.dot} />
                    <p className={'text-[14px] ' + (dark ? 'text-white' : 'text-[#002740] dark:text-slate-200')}>
                      Avatar: <span className="font-bold">{tier.avatar.text}</span>
                    </p>
                  </div>
                  {tier.features.map((f) => (
                    <div key={f.label + f.value} className="flex items-center gap-3">
                      <Check
                        className={'h-4 w-4 shrink-0 ' + (dark ? 'text-[#66a3ff]' : 'text-[#0255ff] dark:text-[#5b8cff]')}
                        strokeWidth={3}
                      />
                      <p className={'text-[14px] ' + (dark ? 'text-white' : 'text-[#002740] dark:text-slate-200')}>
                        {f.label}
                        <span className="font-bold">{f.value}</span>
                        {f.tail}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA — Gói Pro là Cab+ Pro thật (mua bằng Ca ví); Lite/Plus còn placeholder */}
              {tier.key === 'pro' ? (
                <div className="flex flex-col gap-2 pt-6">
                  {status?.isPremium && (
                    <div className="rounded-xl border border-emerald-400/40 bg-emerald-500/15 px-3 py-2 text-center text-[13px]">
                      <span className="font-semibold text-emerald-300">Đang là Cab+ Pro ✓</span>
                      {untilStr && <span className="text-white/70"> · hết hạn {untilStr}</span>}
                      {slot && (
                        <div data-testid="membership-slot" className="mt-1 text-white/80">
                          {slot.used
                            ? `Lượt hội viên tháng này: đã tặng cho ${slot.usedForCreatorName ?? 'một creator'}`
                            : `Lượt hội viên tháng này: còn, dùng trước ${formatMemberUntil(slot.validTo)}`}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] text-white/70">Số tháng</span>
                    <select
                      value={months}
                      onChange={(e) => setMonths(Number(e.target.value))}
                      className="flex-1 rounded-md border border-white/20 bg-white/10 px-3 py-2 text-sm text-white outline-none"
                    >
                      {monthOptions.map((m) => {
                        const p = plans.find((x) => x.months === m);
                        return (
                          <option key={m} value={m} className="text-black">
                            {m} tháng{p && p.discountPercent > 0 ? ` — giảm ${p.discountPercent}%` : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="text-[13px] text-white/70">
                    {(selectedPlan?.pricePerMonthCa ?? status?.pricePerMonthCa)?.toLocaleString('vi-VN') || '—'} Ca/tháng ·{' '}
                    {discountPercent > 0 && (
                      <span className="text-white/40 line-through mr-1">{originalTotal.toLocaleString('vi-VN')}</span>
                    )}
                    <b className="text-white">Tổng {total.toLocaleString('vi-VN')} Ca</b>
                    {discountPercent > 0 && (
                      <span className="ml-1 rounded bg-emerald-500/90 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                        Tiết kiệm {(originalTotal - total).toLocaleString('vi-VN')} Ca (-{discountPercent}%)
                      </span>
                    )}
                  </div>
                  {isHigher && (
                    <div className="text-[12px] text-amber-300">Số ngày còn lại của gói hiện tại được quy đổi sang gói mới</div>
                  )}
                  <div className="text-[12px] text-white/50">
                    Số dư Ca: {status?.balanceCa?.toLocaleString('vi-VN') ?? '—'}
                  </div>
                  <button
                    type="button"
                    onClick={handleBuyPro}
                    disabled={buying}
                    className="w-full rounded-full bg-[#066bff] py-2 text-[16px] font-medium text-white transition hover:brightness-110 disabled:opacity-50"
                  >
                    {buying ? 'Đang xử lý…' : status?.isPremium ? 'Gia hạn Cab+ Pro' : 'Mua Cab+ Pro'}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 pt-8">
                  {isCurrent && (
                    <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-center text-[13px] font-semibold text-emerald-700 dark:text-emerald-300">
                      Đang dùng{curUntilStr ? ` đến ${curUntilStr}` : ''}
                    </div>
                  )}
                  {isLower && (
                    <div className="text-center text-[13px] text-[#58657a] dark:text-slate-400">Bạn đang dùng gói cao hơn</div>
                  )}
                  {isHigher && (
                    <div className="text-center text-[12px] text-amber-600 dark:text-amber-300">
                      Số ngày còn lại của gói hiện tại được quy đổi sang gói mới
                    </div>
                  )}
                  <div className="text-[12px] text-[#58657a] dark:text-slate-400">
                    Số dư Ca: {status?.balanceCa?.toLocaleString('vi-VN') ?? '—'}
                  </div>
                  <button
                    type="button"
                    disabled={isLower || buying}
                    onClick={() => handleOpenConfirm(tierKey as 'lite' | 'plus')}
                    className="w-full rounded-full bg-[#066bff] py-2 text-[16px] font-medium text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:bg-black/5 disabled:text-[#58657a] disabled:opacity-100 dark:disabled:bg-white/10 dark:disabled:text-slate-400"
                  >
                    {isCurrent ? 'Gia hạn thêm 1 tháng' : `Mua gói · ${fmtNum(priceOf(tierKey))} Ca / tháng`}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Comparison table */}
      <div className="overflow-hidden rounded-2xl border border-[#eaecf0] bg-white dark:border-slate-700 dark:bg-slate-800">
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            {/* Header */}
            <div className="grid grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,1fr))] items-center px-6 py-5">
              <span className="text-[16px] font-bold text-[#002740] dark:text-white">
                Tính năng chi tiết
              </span>
              <span className="text-center text-[16px] font-semibold text-[#002740] dark:text-white">Lite</span>
              <span className="text-center text-[16px] font-semibold text-primary">Plus ★</span>
              <span className="text-center text-[16px] font-semibold text-[#002740] dark:text-white">Pro</span>
            </div>
            {/* Rows */}
            {comparison.map((row) => (
              <div
                key={row.label}
                className="grid grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,1fr))] items-center border-t border-[#eaecf0] px-6 py-4 dark:border-slate-700"
              >
                <span className="text-[14px] text-[#58657a] dark:text-slate-400">{row.label}</span>
                {row.cells.map((cell, i) => (
                  <div key={i} className="flex items-center justify-center">
                    {typeof cell === 'string' ? (
                      <span
                        className={
                          'text-[14px] ' +
                          (row.strong
                            ? 'font-bold text-[#002740] dark:text-white'
                            : 'text-[#002740] dark:text-slate-200')
                        }
                      >
                        {cell}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 text-[14px] text-[#002740] dark:text-slate-200">
                        <Dot color={cell.dot} />
                        {cell.text}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Avatar tier dark card */}
      <div className="rounded-3xl border border-white/10 bg-[#19052f] p-8">
        <div className="flex flex-col gap-2">
          {/* Card is always dark (bg-[#19052f]); global.css forces headings to
              !text-black in light mode, so force white here to beat that !important. */}
          <h2 className="text-[22px] font-bold !text-white">
            Bảng phân bậc Avatar &amp; Vật phẩm
          </h2>
          <p className="text-[14px] text-white/60">
            Sử dụng Ca để mua trực tiếp khung Avatar &amp; khung tên theo từng bậc, có thời hạn sử dụng.
          </p>
        </div>

        <div className="mt-6 overflow-x-auto">
          <div className="min-w-[720px] overflow-hidden rounded-xl border border-white/10">
            {/* Header */}
            <div className="grid grid-cols-[minmax(0,180px)_120px_minmax(0,1fr)_160px_180px] border-b border-white/10 bg-white/5 px-5 py-4 text-[14px] font-semibold text-white/80">
              <span>Bậc</span>
              <span>Màu sắc</span>
              <span>Cơ chế áp dụng</span>
              <span>Giá vật phẩm</span>
              <span>Giới hạn phát hành</span>
            </div>
            {/* Rows */}
            {AVATAR_TIERS.map((row, idx) => (
              <div
                key={row.name}
                className={
                  'grid grid-cols-[minmax(0,180px)_120px_minmax(0,1fr)_160px_180px] items-center px-5 py-4 ' +
                  (idx < AVATAR_TIERS.length - 1 ? 'border-b border-white/5' : '')
                }
              >
                <span className="flex items-center gap-2 text-[14px] font-semibold text-white">
                  <Dot color={row.dot} />
                  {row.name}
                </span>
                <span className="text-[14px] text-white/60">{row.color}</span>
                <span className={'text-[14px] text-white ' + (row.mechanismClass || 'font-semibold')}>
                  {row.mechanism}
                </span>
                <span className="text-[14px] font-semibold text-[#a8d7ff]">{row.price}</span>
                <span className={'text-[14px] ' + (row.limitClass || 'text-white/40')}>{row.limit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {confirmTier && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Xác nhận mua gói"
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 px-4"
          onClick={() => !buying && setConfirmTier(null)}
        >
          <div
            className="w-full max-w-[420px] rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-[18px] font-bold text-[#002740] dark:text-white">Xác nhận mua {TIER_NAME[confirmTier]}</h3>
            <div className="mt-3 flex flex-col gap-1 text-[14px] text-[#002740] dark:text-slate-200">
              <div>
                Giá: <b>{fmtNum(priceOf(confirmTier))} Ca</b>
              </div>
              <div>Thời hạn 1 tháng</div>
              <div>Khung avatar theo hạn gói: {confirmTier === 'lite' ? 'Hiếm' : 'Sử thi'}</div>
              <div>{benefits[confirmTier].datingRequests} lượt kết nối Hẹn hò (cộng dồn)</div>
              <div>{benefits[confirmTier].aiTurns} lượt hỏi trợ lý mỗi tháng</div>
              {curRank > 0 && curRank < TIER_RANK[confirmTier] && (
                <div className="mt-1 text-[13px] text-amber-600 dark:text-amber-300">
                  Số ngày còn lại của gói hiện tại được quy đổi sang gói mới
                </div>
              )}
              <div className="mt-1 text-[12px] text-[#58657a] dark:text-slate-400">
                Số dư Ca: {status?.balanceCa?.toLocaleString('vi-VN') ?? '—'}
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                disabled={buying}
                onClick={() => setConfirmTier(null)}
                className="rounded-full border border-[#eaecf0] px-4 py-2 text-[14px] text-[#002740] dark:border-slate-600 dark:text-slate-200"
              >
                Hủy
              </button>
              <button
                type="button"
                disabled={buying}
                onClick={() => doBuyTier(confirmTier)}
                className="rounded-full bg-[#066bff] px-4 py-2 text-[14px] font-medium text-white hover:brightness-110 disabled:opacity-50"
              >
                {buying ? 'Đang xử lý…' : 'Xác nhận mua'}
              </button>
            </div>
          </div>
        </div>
      )}

      <PolicyConsentModal
        open={policyOpen}
        policyKey="cabplus"
        onClose={() => setPolicyOpen(false)}
        onAccepted={() => {
          setPolicyOpen(false);
          setCabplusConsent(true);
          if (consentFor === 'pro') doBuyPro();
          else setConfirmTier(consentFor);
        }}
      />
    </div>
  );
};

export default CabPlusPage;
